import React, { useState, useRef, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { currentConfig } from "../config";

export const useAnalytics = () => {
  const auth = useAuth();
  const [isTracking, setIsTracking] = useState(false);
  const sessionIdRef = useRef(`session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

  const trackEvent = useCallback(async (eventType, eventAttributes = {}) => {
    if (!auth?.isAuthenticated || !auth?.user?.id_token) {
      console.warn('Analytics: User not authenticated, skipping event tracking');
      return;
    }

    setIsTracking(true);

    try {
      // Get user email/ID from OIDC auth context
      const userEmail = auth?.user?.profile?.email || 
                       auth?.user?.profile?.username || 
                       auth?.user?.profile?.preferred_username ||
                       auth?.user?.profile?.sub ||
                       'amazon-employee';

      console.log('Analytics: Tracking event for user:', userEmail);

      const apiUrl = currentConfig.apiUrl;
      const response = await fetch(`${apiUrl}/analytics`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${auth.user.id_token}`,
        },
        body: JSON.stringify({
          action: "track",
          eventData: {
            eventType,
            eventAttributes: {
              ...eventAttributes,
              userEmail: userEmail, // Include user email in event attributes
            },
            sessionId: sessionIdRef.current,
            timestamp: new Date().toISOString(),
            userId: userEmail, // Include user ID directly in event data
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Analytics API error: ${response.status}`);
      }

      const result = await response.json();
      console.log('Analytics event tracked:', { eventType, eventAttributes, result });
      
      return result;
    } catch (error) {
      console.error('Analytics tracking error:', error);
      // Don't throw - analytics failures shouldn't break the app
    } finally {
      setIsTracking(false);
    }
  }, [auth]);

  // Convenience methods for common events
  const trackPageView = useCallback((pageName, additionalAttributes = {}) => {
    return trackEvent('page_view', {
      page: pageName,
      ...additionalAttributes
    });
  }, [trackEvent]);

  const trackScenarioStarted = useCallback((scenario, mood, difficulty = null) => {
    return trackEvent('scenario_started', {
      scenario,
      mood,
      difficulty,
      userEmail: auth?.user?.profile?.email || auth?.user?.profile?.username || 'amazon-employee'
    });
  }, [trackEvent, auth]);

  const trackScenarioCompleted = useCallback((scenario, mood, messageCount, duration) => {
    return trackEvent('scenario_completed', {
      scenario,
      mood,
      messageCount,
      duration,
      userEmail: auth?.user?.profile?.email || auth?.user?.profile?.username || 'amazon-employee'
    });
  }, [trackEvent, auth]);

  const trackMessageSent = useCallback((agentType, messageLength = null) => {
    return trackEvent('message_sent', {
      agentType,
      messageLength,
      page: agentType === 'roleplay' ? 'ScenarioPractice' : 'ChatCoachAI'
    });
  }, [trackEvent]);

  const trackFeedbackRequested = useCallback((scenario, mood, messageCount) => {
    return trackEvent('feedback_requested', {
      scenario,
      mood,
      messageCount,
      userEmail: auth?.user?.profile?.email || auth?.user?.profile?.username || 'amazon-employee'
    });
  }, [trackEvent, auth]);

  const trackSessionStart = useCallback((page) => {
    return trackEvent('session_start', {
      page,
      sessionId: sessionIdRef.current
    });
  }, [trackEvent]);

  const trackSessionEnd = useCallback((page, duration) => {
    return trackEvent('session_end', {
      page,
      duration,
      sessionId: sessionIdRef.current
    });
  }, [trackEvent]);

  const trackButtonClick = useCallback((buttonName, page, additionalAttributes = {}) => {
    return trackEvent('button_click', {
      buttonName,
      page,
      ...additionalAttributes
    });
  }, [trackEvent]);

  const trackScenarioSelection = useCallback((scenario, previousScenario = null) => {
    return trackEvent('scenario_selection', {
      scenario,
      previousScenario
    });
  }, [trackEvent]);

  const trackMoodSelection = useCallback((mood, previousMood = null) => {
    return trackEvent('mood_selection', {
      mood,
      previousMood
    });
  }, [trackEvent]);

  return {
    trackEvent,
    trackPageView,
    trackScenarioStarted,
    trackScenarioCompleted,
    trackMessageSent,
    trackFeedbackRequested,
    trackSessionStart,
    trackSessionEnd,
    trackButtonClick,
    trackScenarioSelection,
    trackMoodSelection,
    isTracking,
    sessionId: sessionIdRef.current
  };
};

// Hook for page tracking with automatic session management
export const usePageTracking = (pageName) => {
  const { trackPageView, trackSessionStart, trackSessionEnd } = useAnalytics();
  const sessionStartTimeRef = useRef(Date.now());

  React.useEffect(() => {
    // Track page view and session start
    trackPageView(pageName);
    trackSessionStart(pageName);
    
    sessionStartTimeRef.current = Date.now();

    // Track session end on cleanup
    const recordSessionEnd = () => {
      const sessionEndTime = Date.now();
      const durationInSeconds = Math.round(
        (sessionEndTime - sessionStartTimeRef.current) / 1000
      );
      trackSessionEnd(pageName, durationInSeconds);
    };

    // Listen for browser unload
    const handleBeforeUnload = () => recordSessionEnd();
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      recordSessionEnd();
    };
  }, [pageName, trackPageView, trackSessionStart, trackSessionEnd]);
};
